import React, { useEffect, useRef, useState } from 'react';
import client, { apiError } from '../api/client';
import Markdown from './Markdown';

export default function ChatWindow() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [disclaimer, setDisclaimer] = useState(
    'AI-generated content — verify against the official PMI ECO and PMBOK® Guide.'
  );
  const endRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await client.get('/chat/sessions');
      setSessions(data.sessions);
      if (data.sessions.length && !activeId) selectSession(data.sessions[0].id);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const selectSession = async (id) => {
    setActiveId(id);
    setError('');
    try {
      const { data } = await client.get(`/chat/sessions/${id}/messages`);
      setMessages(data.messages);
      if (data.disclaimer) setDisclaimer(data.disclaimer);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const newSession = async () => {
    try {
      const { data } = await client.post('/chat/sessions', {});
      setSessions((s) => [data.session, ...s]);
      setActiveId(data.session.id);
      setMessages([]);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await client.delete(`/chat/sessions/${id}`);
      const remaining = sessions.filter((s) => s.id !== id);
      setSessions(remaining);
      if (activeId === id) {
        setMessages([]);
        setActiveId(remaining[0] ? remaining[0].id : null);
        if (remaining[0]) selectSession(remaining[0].id);
      }
    } catch (err) {
      setError(apiError(err));
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');

    let sessionId = activeId;
    if (!sessionId) {
      try {
        const { data } = await client.post('/chat/sessions', {});
        sessionId = data.session.id;
        setSessions((s) => [data.session, ...s]);
        setActiveId(sessionId);
      } catch (err) {
        setError(apiError(err));
        return;
      }
    }

    const userMsg = { id: `tmp-${Date.now()}`, role: 'user', content: text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const { data } = await client.post(`/chat/sessions/${sessionId}/messages`, { message: text });
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: 'assistant', content: data.reply, citations: data.citations },
      ]);
      loadSessions();
    } catch (err) {
      setError(apiError(err, 'Could not get a reply. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  return (
    <div className="chat-layout">
      <div>
        <button className="btn btn-primary btn-block btn-sm" onClick={newSession}>
          + New session
        </button>
        <div className="divider" />
        <div className="session-list">
          {sessions.length === 0 && <div className="small muted">No sessions yet.</div>}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`session-item ${activeId === s.id ? 'active' : ''}`}
              onClick={() => selectSession(s.id)}
            >
              <div className="row-between">
                <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.title}
                </span>
                <span onClick={(e) => deleteSession(s.id, e)} title="Delete" style={{ color: 'var(--muted)' }}>
                  ×
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card chat-window">
        {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
        <div className="messages">
          {messages.length === 0 && !sending && (
            <div className="muted small center" style={{ margin: 'auto', maxWidth: 380 }}>
              Ask anything about the PMP exam — a domain, a task, a tricky scenario, or how to
              study. Answers are grounded in the indexed PMP material.
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`msg ${m.role}`}>
              {m.role === 'assistant' ? <Markdown>{m.content}</Markdown> : m.content}
              {m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                <div className="cites">Sources: {m.citations.join(' · ')}</div>
              )}
            </div>
          ))}
          {sending && (
            <div className="msg assistant">
              <span className="spinner dark" style={{ display: 'inline-block' }} />
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="chat-input">
          <textarea
            className="grow"
            placeholder="Type your question…  (Enter to send, Shift+Enter for a new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
          />
          <button className="btn btn-primary" onClick={send} disabled={sending}>
            Send
          </button>
        </div>
        <div className="disclaimer">{disclaimer}</div>
      </div>
    </div>
  );
}
